import { useEffect, useState } from "react";
import TextInput from "../../../../components/inputs/text";
import TransmedRanking from "../../../../dtos/transmed/process/process/ranking";
import TransmedProcessSlide from "./slide";
import { useSetError } from "../../../generic-error";
import { CircularProgress } from "@mui/material";
import { useRevalidator } from "react-router-dom";
import useWindowDimensions from "../../../../hooks/useWindowDimensions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { CSVLink } from "react-csv";
import subtractOneSecond from "../../../../utils/subtractOneSecond";
import TransmedRegistration from "../../../../dtos/transmed/process/registration/registration";
import TransmedRegistrationContestationService from "../../../../service/transmed/process/registration/contestation";

function TransmedProcessRanking(props: {
  registration: TransmedRegistration;
  ranking: TransmedRanking;
  final: boolean;
}) {
  const allowContesting = () => {
    return (
      props.registration.question === null &&
      new Date() < props.ranking.contesting_deadline
    );
  };
  const [question, setQuestion] = useState<string>(
    props.registration.question ?? ""
  );

  const [rank, setRank] = useState<number>(-1);

  useEffect(() => {
    props.ranking.ranking.sort((a, b) => b.points - a.points);
  }, [props.ranking.ranking]);

  useEffect(
    () =>
      setRank(
        props.ranking.ranking.reduce((bestIdx, curr, idx, array) => {
          const target = props.registration.points ?? 0;
          return Math.abs(curr.points - target) <
            Math.abs(array[bestIdx].points - target)
            ? idx
            : bestIdx;
        }, 0)
      ),
    [props.ranking.ranking, props.registration.points]
  );

  const revalidator = useRevalidator();
  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
  };
  const [loading, setLoading] = useState<boolean>(false);

  const dimensions = useWindowDimensions();

  return (
    <TransmedProcessSlide>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "var(--gap-small)",
        }}
      >
        <h3>Clasament {props.final ? "final" : "provizoriu"}</h3>
        <CSVLink
          data={props.ranking.ranking.map((element, index) => ({
            Poziție: (index + 1).toFixed(0),
            Punctaj: element.points.toFixed(2),
            // "Id privat": index === rank ? getUserPrivateId(auth!) : "(ascuns)",
          }))}
          filename="clasament.csv"
          target="_blank"
          className="primary text button"
        >
          <FontAwesomeIcon icon={faDownload} size="2xl" />
        </CSVLink>
      </div>
      {props.ranking.ranking.length === 0 ? (
        <div
          style={{
            marginTop: "var(--gap-medium)",
          }}
        >
          <b>Validare în curs!</b> Clasamentul va fi disponibil după ce toate
          înscrierile vor fi validate.
        </div>
      ) : (
        <>
          <div
            style={{
              marginTop: "var(--gap-big)",
              display: "flex",
              flexDirection: "row",
              alignContent: "flex-start",
              gap: "var(--gap-small) var(--gap-xl)",
              flexWrap: "wrap",
            }}
          >
            <div>
              <h4>Punctaj</h4>
              <h1 style={{ color: "var(--color-fasmr-blue)" }}>
                {props.registration.points?.toFixed(2)}
              </h1>
            </div>
            <div>
              <h4>Poziția în clasament</h4>
              <h1 style={{ color: "var(--color-fasmr-blue)" }}>
                {rank + 1} / {props.ranking.ranking.length}
              </h1>
            </div>
          </div>
          <div className="label">
            <b>Notă:</b> Punctajul final este diferit de cel estimativ, întrucât
            acesta este calculat în funcție de cel mai mare punctaj obținut de
            un oarecare candidat, pentru fiecare secțiune.
          </div>
          <form
            style={{
              marginTop: "var(--gap-big)",
              display: "flex",
              flexDirection: "column",
              alignContent: "flex-start",
              gap: "var(--gap-small)",
            }}
            onSubmit={async (e) => {
              e.preventDefault();

              setError("");
              setLoading(true);
              try {
                if (question.trim().length === 0)
                  throw new Error("Nu poți contesta fără o întrebare.");
                await new TransmedRegistrationContestationService().create.contestation(
                  question
                );
                revalidator.revalidate();
              } catch (error) {
                setError(error);
              } finally {
                setLoading(false);
              }
            }}
          >
            <span>
              Dacă ești nemulțumit de rezultat sau consideri că s-a produs vreo
              greșală, poți depune o contestație până în:{" "}
              <b>
                {subtractOneSecond(
                  props.ranking.contesting_deadline
                ).toLocaleDateString("RO-ro")}
                , ora{" "}
                {subtractOneSecond(
                  props.ranking.contesting_deadline
                ).toLocaleTimeString("RO-ro")}
              </b>
            </span>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: dimensions.width > 550 ? "1fr 1fr" : "1fr",
                gap: "var(--gap-small)",
              }}
            >
              <h4>Contestație</h4>
              <TextInput
                multiline
                required
                rows={6}
                style={{ gridRowStart: 2, gridRowEnd: 2 }}
                InputProps={{
                  readOnly: !allowContesting(),
                }}
                onKeyDown={(e) => {
                  if (!allowContesting()) e.preventDefault();
                }}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                disabled={false}
                sx={{
                  "& .MuiInputBase-root": {
                    paddingTop: "12px",
                  },
                }}
              />
              <h4>Răspuns</h4>
              <TextInput
                multiline
                rows={6}
                InputProps={{
                  readOnly: true,
                }}
                onKeyDown={(e) => e.preventDefault()}
                value={props.registration.answer}
                sx={{
                  "& .MuiInputBase-root": {
                    paddingTop: "12px",
                  },
                }}
              />
            </div>
            {!props.final && (
              <div style={{ display: "flex", gap: "var(--gap-small)" }}>
                <button
                  type="submit"
                  className="primary button"
                  style={{ alignSelf: "flex-start" }}
                  disabled={!allowContesting()}
                >
                  Depune Contestație
                </button>
                {(loading || revalidator.state === "loading") && (
                  <CircularProgress />
                )}
              </div>
            )}
          </form>
          {!props.final && (
            <h4 style={{ marginTop: "var(--gap-medium)" }}>
              {new Date() < props.ranking.points_deadline_second ? (
                <>
                  Clasamentul final va fi disponibil în:{" "}
                  <b>
                    {props.ranking.points_deadline_second.toLocaleDateString(
                      "RO-ro"
                    )}
                  </b>
                </>
              ) : (
                <>
                  Clasamentul final va fi disponibil îndată ce toate
                  contestațiile sunt soluționate.
                </>
              )}
            </h4>
          )}
        </>
      )}
    </TransmedProcessSlide>
  );
}

export default TransmedProcessRanking;
